import React from 'react';
import { SEO } from '@/components/SEO';
import ServiceSchema from '@/components/services/ServiceSchema';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ServiceHero from '@/components/services/ServiceHero';
import ServiceBenefits from '@/components/services/ServiceBenefits';
import ServiceFAQ from '@/components/services/ServiceFAQ';
import ServiceCTA from '@/components/services/ServiceCTA';
import ServiceCrossLinks from '@/components/services/ServiceCrossLinks';
import { Brush } from 'lucide-react';
import softWash1 from '@/assets/soft-wash-1.webp';
import softWash2 from '@/assets/soft-wash-2.webp';

const SoftWashPage = () => {
  const serviceData = {
    title: "Professional Soft Wash Services in Ventura, CA",
    subtitle: "Gentle Yet Effective Exterior Cleaning for Your Home",
    description: "Brighten your home's exterior and clear your roof of organic matter with our specialized soft wash services. Perfect for delicate surfaces like stucco, wood, and painted exteriors common in Ventura County.",
    icon: <Brush className="h-16 w-16 text-ventura-blue" />,
    beforeImage: softWash1,
    afterImage: softWash2
  };

  const benefits = [
    "Safe for delicate exterior surfaces",
    "Kills mold, mildew, and bacteria at the root",
    "Longer-lasting results than pressure washing",
    "Protects landscaping and plants",
    "Environmentally responsible methods",
    "Ideal for stucco and painted surfaces"
  ];

  const faqs = [
    {
      question: "What's the difference between soft wash and pressure washing?",
      answer: "Soft wash uses low pressure with specialized cleaning solutions to safely clean delicate surfaces like stucco and painted exteriors, which are common in Ventura County homes."
    },
    {
      question: "Is soft washing safe for my Ventura home's landscaping?",
      answer: "Absolutely! Our eco-friendly soft wash solutions are designed to be safe for plants and landscaping, which is especially important for Ventura County's beautiful outdoor spaces."
    },
    {
      question: "How long do soft wash results last?",
      answer: "Soft wash treatments typically last 4-6 times longer than pressure washing because we eliminate organic growth at the source, making it perfect for Ventura's year-round growing season."
    }
  ];

  return (
    <>
      <SEO
        title="Soft Wash Services Ventura CA | Gentle Exterior House Cleaning"
        description="Professional soft wash Ventura & Oxnard CA. Gentle eco-friendly cleaning for stucco, roofs in Camarillo, Thousand Oaks. Kills mold safely!"
        keywords="soft wash Ventura CA, roof cleaning Camarillo, stucco cleaning Thousand Oaks, gentle house washing Ventura County"
        path="/services/soft-wash"
      />
      <ServiceSchema
        serviceName="Soft Wash"
        description="Professional soft wash Ventura & Oxnard CA. Gentle eco-friendly cleaning for stucco, roofs in Camarillo, Thousand Oaks."
        serviceType="Soft Wash"
        areaServed={["Ventura", "Oxnard", "Camarillo", "Thousand Oaks", "Simi Valley", "Fillmore", "Moorpark", "Port Hueneme", "Santa Paula"]}
        additionalServices={["Pressure Washing", "Window Cleaning", "Gutter Cleaning"]}
      />
      
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <ServiceHero {...serviceData} />
        
        <section className="section-padding bg-white">
          <div className="container mx-auto">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Soft Wash vs. Pressure Washing: Which Does Your Ventura County Stucco Home Need?</h2>
              <p className="text-lg text-gray-600 mb-8 text-center">
                Soft washing uses low pressure and biodegradable cleaning solutions instead of high-force water, making it the safer choice for
                stucco, painted wood, and other delicate materials common on Ventura County homes. High pressure can crack stucco or strip
                paint, while soft wash removes the same algae and organic growth without risking damage to these softer surfaces.
              </p>
              
              {/* House Soft Washing Section */}
              <div className="max-w-4xl mx-auto bg-gray-50 p-8 rounded-lg mb-12">
                <h3 className="text-3xl font-bold mb-6 text-center">House Soft Washing in Oxnard</h3>
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div className="before-after-container">
                    <div className="before-after-image">
                      <img 
                        src={softWash1} 
                        alt="Before house soft washing Oxnard - algae and dirt buildup on siding" 
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <span className="before-after-label before-label">Before</span>
                    </div>
                    <div className="before-after-image">
                      <img 
                        src={softWash2} 
                        alt="After house soft washing Oxnard - clean bright home exterior" 
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <span className="before-after-label after-label">After</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-4">
                      Protect your Oxnard home's delicate surfaces with our gentle soft wash technique. Perfect for stucco, painted wood, and vinyl siding common in Oxnard neighborhoods. Our low-pressure method combined with specialized biodegradable solutions safely removes algae, mold, and mildew without damage.
                    </p>
                    <p className="text-gray-600 mb-4">
                      The coastal environment in Oxnard creates ideal conditions for organic growth on home exteriors. Our soft washing process not only cleans but also treats surfaces to prevent rapid regrowth, keeping your home looking fresh longer while preserving the integrity of delicate materials.
                    </p>
                    <div className="bg-primary/10 p-4 rounded-lg mb-4">
                      <h4 className="font-semibold text-primary mb-2">Complete House Soft Washing Starting at $350</h4>
                      <p className="text-sm text-gray-600">Includes full exterior treatment and surface protection</p>
                    </div>
                    <div className="mb-4">
                      <h4 className="font-semibold mb-2">FAQ: Is soft washing safe for my Oxnard home?</h4>
                      <p className="text-gray-600 text-sm">Yes! Our soft wash method uses 80% less pressure than traditional pressure washing, making it safe for all exterior surfaces while being more effective at removing organic growth.</p>
                    </div>
                    <Link to="/#contact" className="text-primary font-semibold hover:underline">
                      Schedule Your Oxnard Soft Wash Service →
                    </Link>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 items-center mb-12">
                <div>
                  <h3 className="text-2xl font-semibold mb-4">Comprehensive Soft Wash Solutions</h3>
                  <p className="text-gray-600 mb-4">
                    Specialized cleaning services for your Ventura County property:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-2">
                    <li>Stucco and painted exterior cleaning</li>
                    <li>Roof soft wash and moss removal</li>
                    <li>Fence and deck restoration</li>
                    <li>Outdoor furniture cleaning</li>
                    <li>Awning and shade structure care</li>
                    <li>Preventive maintenance programs</li>
                  </ul>
                </div>
                <div className="before-after-container">
                  <div className="before-after-image">
                    <img 
                      src={serviceData.beforeImage} 
                      alt="Dirty house exterior before soft wash cleaning in Ventura CA" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <span className="before-after-label before-label">Before</span>
                  </div>
                  <div className="before-after-image">
                    <img 
                      src={serviceData.afterImage} 
                      alt="Clean house exterior after professional soft wash service" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <span className="before-after-label after-label">After</span>
                  </div>
                </div>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-6">Advanced Soft Wash Technology for Delicate Surfaces</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-ventura-blue">Low-Pressure Application</h4>
                    <p className="text-gray-600 mb-4">
                      Our soft wash systems operate at garden hose pressure, making them safe for delicate surfaces throughout Camarillo, Thousand Oaks, and Ventura. This gentle approach effectively cleans stucco, painted wood, and other sensitive materials without causing damage that high-pressure washing might inflict.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-ventura-blue">Specialized Cleaning Solutions</h4>
                    <p className="text-gray-600 mb-4">
                      We use EPA-approved, biodegradable cleaning solutions specifically formulated for different organic contaminants. These solutions kill mold, mildew, algae, and bacteria at the root level, providing longer-lasting results than pressure washing alone for Ventura County's coastal climate challenges.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-6">Perfect for Ventura County's Architectural Styles</h3>
                <p className="text-gray-600 mb-4">
                  Ventura County's Mediterranean and Spanish Colonial Revival architecture features extensive use of stucco, tile roofs, and painted surfaces that require gentle cleaning methods. Traditional pressure washing can damage these delicate finishes, but our soft wash process safely removes years of buildup while preserving the authentic character of your home.
                </p>
                <p className="text-gray-600 mb-4">
                  From the historic adobe structures in Ventura to modern stucco homes in Simi Valley, we understand the unique cleaning requirements of different architectural materials. Our soft wash treatments are particularly effective on textured surfaces where dirt and organic growth can hide in crevices that high-pressure water might miss.
                </p>
                <p className="text-gray-600 mb-6">
                  The coastal influence in areas like Port Hueneme and Oxnard creates ideal conditions for algae and mildew growth on exterior surfaces. Our soft wash process not only removes these organisms but treats the surface to prevent rapid regrowth, keeping your home looking cleaner longer in these challenging environments.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg mb-12">
                <h3 className="text-2xl font-semibold mb-4">Roof Soft Wash Excellence</h3>
                <p className="text-gray-600 mb-4">
                  Roof cleaning in Ventura County requires special expertise due to the variety of roofing materials and the potential for moisture-related growth in our coastal climate. Our roof soft wash service safely removes moss, lichen, and algae from tile, asphalt, and metal roofing without the risk of damage associated with pressure washing.
                </p>
                <p className="text-gray-600">
                  Regular roof maintenance through soft washing not only improves your home's appearance but also extends roof life by eliminating organisms that can cause material degradation. This preventive approach is especially important for the clay tile roofs common throughout Ventura County, where traditional cleaning methods might crack or dislodge tiles.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-semibold mb-4">Environmental Safety and Landscape Protection</h3>
                <p className="text-gray-600 mb-4">
                  Our soft wash process is designed with Ventura County's beautiful landscaping in mind. All cleaning solutions are thoroughly diluted and applied in a controlled manner that minimizes impact on surrounding vegetation. We take special care to protect sensitive plants, flowers, and trees that make our region's outdoor spaces so special.
                </p>
                <p className="text-gray-600">
                  Before beginning any soft wash project, we pre-wet and protect landscaping, ensuring that your carefully maintained gardens in areas like Fillmore and Santa Paula remain unharmed. Our eco-friendly approach aligns with California's environmental values while delivering the thorough cleaning results your property deserves.
                </p>
              </div>
            </div>
          </div>
        </section>

        <ServiceBenefits benefits={benefits} />
        <ServiceCrossLinks 
          currentService="Soft Wash"
          relatedServices={[
            { name: 'Pressure Washing', path: '/services/pressure-washing', icon: 'pressure' },
            { name: 'Window Cleaning', path: '/services/window-washing', icon: 'window' },
            { name: 'Gutter Cleaning', path: '/services/gutter-cleaning', icon: 'gutter' },
          ]}
        />
        <ServiceFAQ faqs={faqs} serviceName="Soft Wash" />
        <ServiceCTA 
          title="Ready for Gentle, Effective Cleaning?"
          description="Schedule your professional soft wash service and restore your Ventura County home's exterior beauty safely and effectively."
        />
        <Footer />
      </div>
    </>
  );
};

export default SoftWashPage;